import { useMemo } from 'react';
import { useRouter } from 'next/compat/router';
import _ from 'lodash';

import { useFlags } from 'launchdarkly-react-client-sdk';

import useDispensaryDeliveryInfo from 'dispensary/hooks/use-dispensary-delivery-info';
import { getLogger } from 'src/utils/datadog-logger';
import { useEnterpriseStorefrontContext } from 'src/dispensary/enterprise-storefront-context';

import { consumerDispensaries } from 'shared/graphql/dispensary/queries';
import { useQuery } from 'hooks/use-query';

const logger = getLogger('use-dispensary');

export const getQuery = () => consumerDispensaries;

export const getVariables = (dispensaryId, filters) => ({
  dispensaryFilter: {
    cNameOrID: dispensaryId,
    ...filters,
  },
});

export const getDispensary = async (apolloClient, props) =>
  apolloClient.query({
    query: getQuery(),
    variables: getVariables(props.params.cName),
    skip: !props.params.cName,
  });

function getError(query) {
  const params = Object.keys(query).join(', ');
  const error = new Error(
    `useDispensary may only be called from a route with a cName parameter. Found keys: ${params}`
  );
  error.query = query;

  return error;
}

export default function useDispensary(id) {
  const { isReady, query } = useRouter() ?? {};
  const enterpriseStorefront = useEnterpriseStorefrontContext();
  const flags = useFlags();
  const disableHeadFallback = flags['core.ecomm.disable-dispensary-head-fallback.rollout'] ?? false;
  // Allowlist of dispensary cNames/ids that use the corrected matchedDispensary comparison below.
  // Evaluated against the router-derived dispensaryId rather than LD's ecomm-dispensary context,
  // because that context is populated from this hook's own output -- on a cold load the wrong
  // sibling location can be identified to LD, so context targeting cannot be trusted here.
  const cNameMatchFixAllowlist = flags['growth.ecomm.fix-dispensary-cname-match.entitlement'] ?? [];

  const { cName, dispensaryId: dispoIdFromQuery, cname } = query ?? {};
  /**
   * Context for different cname identifier params:
   * - cName: The canonical dispensary name used in the main URL structure
   * - cname: Alternative lowercase variant used specifically in pay/signup routes query params
   * We need to support both casing variants (cName and cname) to ensure all route patterns
   * can correctly identify the dispensary regardless of which URL pattern was used to access it.
   */
  // Clean enterprise-domain pages have no cName route parameter, but still require
  // a store for theming, interpolation, navigation, cart, and commerce blocks.
  // Explicit and route-derived choices remain authoritative; the server-selected
  // enterprise storefront value is only the final fallback.
  const dispensaryId = id || cName || dispoIdFromQuery || cname || enterpriseStorefront?.dispensaryId;

  const { data, loading, error, ...apolloValues } = useQuery(consumerDispensaries, {
    variables: getVariables(dispensaryId),
    skip: !isReady || !dispensaryId,
  });

  // Comparing against dispensaryId (which already merges cName/dispoIdFromQuery/cname, see comment
  // above) instead of the raw cName param fixes routes like /pay/signup that only pass the
  // lowercase `cname` variant: matching on `cName` alone always misses there and falls through to
  // the _.head() fallback below, which can silently select the wrong sibling location for chain
  // dispensaries, since cNameOrID lookups can return multiple chain locations for one cName/id.
  // Array.isArray guards against a malformed flag variation: a non-array value would make
  // .includes either substring-match or throw, and this hook runs on nearly every page.
  const useDispensaryIdForCNameMatch =
    !!dispensaryId && Array.isArray(cNameMatchFixAllowlist) && cNameMatchFixAllowlist.includes(dispensaryId);
  const matchedDispensary = _.find(data?.filteredDispensaries, (d) =>
    useDispensaryIdForCNameMatch
      ? d.cName === dispensaryId || d.id === dispensaryId
      : d.cName === cName || d.id === dispensaryId
  );

  // ECOMM-9255: If the API returned results but none matched, the previous _.head() fallback
  // silently selected the first dispensary in the list, causing orders on chain storefronts to
  // be routed to the wrong location (e.g., Ashland instead of the customer's selected store).
  // When the flag is enabled, return undefined instead of guessing.
  let dispensary = matchedDispensary;
  if (!matchedDispensary && data?.filteredDispensaries?.length > 0) {
    logger.error('dispensary_lookup_mismatch', {
      dispensaryId: dispensaryId || 'none',
      cName: cName || 'none',
      resultCount: data.filteredDispensaries.length,
      resultCNames: data.filteredDispensaries.map((d) => d.cName).join(','),
    });

    if (!disableHeadFallback) {
      dispensary = _.head(data?.filteredDispensaries);
    }
  }

  // Only fetch delivery info if we have the actual dispensary object with its numeric ID
  // dispensaryId from router might be a cName string like 'dr-jollys', but API needs numeric ID
  const actualDispensaryId = dispensary?.id;
  const deliveryInfoResult = useDispensaryDeliveryInfo(actualDispensaryId || null);

  const finalDispensary = useMemo(() => {
    if (!deliveryInfoResult || !dispensary) {
      return dispensary;
    }
    return {
      ...dispensary,
      deliveryInfo: deliveryInfoResult.deliveryInfo || dispensary.deliveryInfo,
      taxConfig: {
        ...dispensary.taxConfig,
        taxes: deliveryInfoResult.taxes || dispensary.taxConfig.taxes,
      },
    };
  }, [dispensary, deliveryInfoResult]);

  if (isReady && !dispensaryId) {
    return {
      data,
      loading,
      error: getError(query),
      dispensary: finalDispensary,
      ...apolloValues,
    };
  }

  return {
    data,
    loading: !isReady || loading,
    error,
    dispensary: finalDispensary,
    dispensaryId,
    ...apolloValues,
  };
}
